import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Checkbox, Chip, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { ISelectedFile } from "components/File-repository/FileRepository";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import {
  useActionHook,
  useFormatDateHook,
  useGetGroupNames,
  useModals,
  useNavigate,
  useNavigationHooks,
  usePrompt,
  useUrlHooks
} from "hooks";
import { throttle } from "lodash";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { useDealActions, useUserRoleSelector } from "store";
import { IFile, IFileRepo, Order } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { FileType, RoleName } from "../../../constants";
import { ClassificationPrompt } from "./ClassificationPrompt";
import { CurrencyPrompt } from "./CurrencyPrompt";
import { DealPrompt } from "./DealPrompt";
import { EffectiveDatePrompt } from "./EffectiveDatePrompt";
import { FileRepoIcon } from "./FileRepoIcon";
import { InvestorPrompt } from "./InvestorPrompt";
import { getStatusConfig, StatusPrompt } from "./StatusPrompt";

const RESERVED_HEIGHT = 150;
export const AddValueButton: React.FC<{
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}> = ({ onClick, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px",
      borderRadius: "32px",
      cursor: disabled ? "default" : "pointer",
      transition: "padding 0.2s ease, background-color 0.2s ease",
      bgcolor: "#f5f6f7",
      opacity: disabled ? 0.5 : 1,
      "& .add-value-label": {
        maxWidth: 0,
        opacity: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
        fontSize: "12px",
        color: "#071f35",
        lineHeight: 1,
        transition: "max-width 0.2s ease, opacity 0.2s ease"
      },
      ...(!disabled && {
        "&:hover": {
          padding: "4px 8px",
          bgcolor: "rgba(7, 31, 53, 0.04)",
          "& .add-value-label": {
            maxWidth: "80px",
            opacity: 1
          },
          "& .add-value-icon": {
            color: "#071f35"
          }
        }
      })
    }}
  >
    <AddIcon className="add-value-icon" sx={{ fontSize: 16, color: "#8e99a3" }} />
    <Box component="span" className="add-value-label">
      Add Value
    </Box>
  </Box>
);

export const EditableCell: React.FC<{
  value: string;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  color?: string;
  disabled?: boolean;
  tooltip?: string;
}> = ({ value, onClick, color = "#26a6ae", disabled = false, tooltip }) => (
  <MaterialTooltip title={tooltip ?? value} isShow>
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        cursor: disabled ? "default" : "pointer",
        overflow: "hidden",
        "& .edit-icon": { opacity: 0, transition: "opacity 0.15s ease", backgroundColor: "#f5f6f7" },
        "&:hover .edit-icon": { opacity: 1 }
      }}
    >
      <Typography noWrap sx={{ display: "block", color, minWidth: 0, flex: "1 1 auto" }}>
        {value}
      </Typography>
      {!disabled && (
        <Chip
          className="edit-icon"
          icon={<EditOutlinedIcon sx={{ fontSize: 14, color: "#496682 !important" }} />}
          sx={{
            height: 24,
            "& .MuiChip-label": { display: "none" },
            "& .MuiChip-icon": { mx: 0 },
            minWidth: 32,
            justifyContent: "center"
          }}
        />
      )}
    </Box>
  </MaterialTooltip>
);

const sortColumnToHeader: Record<string, string> = {
  name: "Name",
  deal: "Deal",
  document_type: "Altius Classification",
  created_at: "Upload Date"
};

interface IFilesTableProps {
  filesIds: number[];
  setFile: (file: ISelectedFile) => void;
  onSelectAll: (checked: boolean) => void;
  files: IFileRepo[];
  isDataLoading: boolean;
  hasMore: boolean;
  getNext: () => void;
  refetch: () => void;
  updateFile: (fileId: number, updates: Partial<IFileRepo>) => void;
  removeFile: (fileId: number) => void;
  sorting: { order: Order | null; orderBy: string | null };
  handleSort: (columnKey: string) => void;
  isLoading: boolean;
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  // Highlight-after-close is owned by the parent so the bulk actions bar can share the same highlight.
  highlightedFileId: number | null;
  lastViewedIdRef: React.MutableRefObject<number | null>;
  triggerHighlight: () => void;
}

const READ_ONLY_ROLES = [RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer];

export const FilesTable: React.FC<IFilesTableProps> = ({
  filesIds,
  setFile,
  onSelectAll,
  files,
  isDataLoading,
  hasMore,
  getNext,
  refetch,
  updateFile: updateFileProp,
  removeFile,
  sorting,
  handleSort,
  isLoading,
  columnVisibility,
  onColumnVisibilityChange,
  highlightedFileId,
  lastViewedIdRef: lastViewedFileIdRef,
  triggerHighlight
}) => {
  const role = useUserRoleSelector();
  const isReadOnlyRole = Boolean(role?.name && READ_ONLY_ROLES.includes(role.name as RoleName));
  const formatDate = useFormatDateHook();
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { deleteFileAction, downloadFileAction, updateFileRepoAction } = useDealActions();
  const { action: updateFile } = useActionHook(updateFileRepoAction, {
    onSuccess: (file) => {
      updateFileProp(file.id, file);
    }
  });
  const { deleteModal } = useModals();
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingNextRef = useRef(false);
  const [filePreview, setFilePreview] = useState<Partial<IFile> | null>(null);
  const { queryParams } = useUrlHooks();

  const getGroupNames = useGetGroupNames();

  const getSelectedFileIds = useCallback(
    (clickedFileId: number): number[] => {
      if (filesIds.length) {
        const selectedIds = files.filter((f) => filesIds.includes(f.id) && f.type === FileType["PDF"]).map((f) => f.id);
        if (selectedIds.length) {
          const idx = selectedIds.indexOf(clickedFileId);
          if (idx > 0) {
            selectedIds.splice(idx, 1);
            selectedIds.unshift(clickedFileId);
          }
          return selectedIds;
        }
      }
      return [clickedFileId];
    },
    [filesIds, files]
  );
  const getFileNamesMap = useCallback(
    (ids: number[]): Record<number, string> => {
      const map: Record<number, string> = {};
      ids.forEach((id) => {
        const file = files.find((f) => f.id === id);
        if (file) map[id] = file.name;
      });
      return map;
    },
    [files]
  );

  const openDataExtractor = useCallback(
    async (fileId: number, options?: { initialShowParameters?: boolean }) => {
      const fileIds = getSelectedFileIds(fileId);
      lastViewedFileIdRef.current = fileId;

      await openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds,
          fileNames: getFileNamesMap(fileIds),
          source: "fileRepository" as const,
          onUpdateFile: updateFileProp,
          onCloseWithFileId: (currentFileId: number) => {
            lastViewedFileIdRef.current = currentFileId;
          },
          ...(options?.initialShowParameters !== undefined && {
            initialShowParameters: options.initialShowParameters
          })
        }
      });

      triggerHighlight();
    },
    [getSelectedFileIds, getFileNamesMap, openPrompt, updateFileProp, triggerHighlight, lastViewedFileIdRef]
  );

  const { removeParams } = useNavigationHooks();

  const onScroll = useCallback(
    (e: Event) => {
      if (isDataLoading || isFetchingNextRef.current) return;
      if (!hasMore) return;

      const { scrollHeight, clientHeight, scrollTop } = e.target as HTMLDivElement;
      const shouldGetNext = scrollHeight - clientHeight - scrollTop - RESERVED_HEIGHT <= 0;

      if (shouldGetNext) {
        isFetchingNextRef.current = true;
        getNext();
      }
    },
    [hasMore, getNext, isDataLoading]
  );

  const throttleOnScroll = useCallback(throttle(onScroll, 200), [onScroll]);

  const onDelete = useCallback(
    (fileId: number, dealId: number, fileName: string) => {
      deleteModal({
        action: async () => {
          await deleteFileAction({ id: fileId, deal_id: dealId } as IFile);
          if (filesIds.includes(fileId)) {
            const file = files.find((f) => f.id === fileId);
            if (file) {
              setFile({
                id: file.id,
                type: file.type,
                name: file.name,
                document_type: file.document_type || null,
                investor_id: file.investor?.id ?? null,
                deal_id: file.deal?.id ?? null,
                date: file.created_at ?? null
              });
            }
          }
          removeFile(fileId);
        },
        element: "Delete File",
        message: `Are you sure you want to delete file ${fileName}?`
      });
    },
    [deleteModal, deleteFileAction, removeFile, filesIds, files, setFile]
  );

  useEffect(() => {
    if (queryParams.refetchFileRepository) {
      refetch();
      removeParams(["refetchFileRepository"]);
    }
  }, [queryParams, removeParams, refetch]);

  useEffect(() => {
    if (!isDataLoading) {
      isFetchingNextRef.current = false;
    }
  }, [isDataLoading]);

  useEffect(() => {
    const { current } = containerRef;

    if (current) {
      current.addEventListener("scroll", throttleOnScroll);
    }
    return () => current?.removeEventListener("scroll", throttleOnScroll);
  }, [throttleOnScroll]);

  // Preserve scroll position during column resizing with row virtualization
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".Mui-TableHeadCell-ResizeHandle-Wrapper")) return;

      const savedScrollTop = container.scrollTop;

      const preventScrollReset = () => {
        if (container.scrollTop !== savedScrollTop) {
          container.scrollTop = savedScrollTop;
        }
      };

      container.addEventListener("scroll", preventScrollReset);

      const handleMouseUp = () => {
        setTimeout(() => {
          container.removeEventListener("scroll", preventScrollReset);
        }, 100);
        window.removeEventListener("mouseup", handleMouseUp);
      };
      window.addEventListener("mouseup", handleMouseUp);
    };

    container.addEventListener("mousedown", handleMouseDown);
    return () => container.removeEventListener("mousedown", handleMouseDown);
  }, []);

  const columns = useMemo<MRT_ColumnDef<IFileRepo>[]>(
    () => [
      {
        id: "select",
        header: "",
        size: 43,
        minSize: 43,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enableHiding: false,
        muiTableHeadCellProps: {
          sx: {
            padding: "0 0 0 12px !important",
            borderBottom: "1px solid #dbe8f5 !important",
            "& .Mui-TableHeadCell-Content": {
              justifyContent: "flex-start",
              alignItems: "center",
              height: "100%",
              padding: 0,
              margin: 0
            },
            "& .Mui-TableHeadCell-Content-Labels": { padding: 0, display: "flex", alignItems: "center" },
            "& .Mui-TableHeadCell-Content-Wrapper": {
              overflow: "visible",
              minWidth: "unset",
              textOverflow: "clip",
              padding: 0,
              display: "flex",
              alignItems: "center"
            }
          }
        },
        Header: () => {
          const allSelected = files.length > 0 && filesIds.length === files.length;
          const someSelected = filesIds.length > 0 && filesIds.length < files.length;
          return (
            <MaterialTooltip title={allSelected ? "Deselect all" : "Select all"} isShow>
              <Checkbox
                size="small"
                disableRipple
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                sx={{
                  "&:hover": { backgroundColor: "transparent" },
                  padding: 0
                }}
              />
            </MaterialTooltip>
          );
        },
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            disableRipple
            sx={{
              "&:hover": { backgroundColor: "transparent" },
              padding: 0
            }}
            onChange={() =>
              setFile({
                id: row.original.id,
                type: row.original.type,
                name: row.original.name,
                document_type: row.original.document_type || null,
                investor_id: row.original.investor?.id ?? null,
                deal_id: row.original?.deal?.id ?? null,
                date: row.original.created_at ?? null
              })
            }
            checked={filesIds.includes(row.original.id)}
          />
        )
      },
      {
        id: "indicators",
        header: "",
        size: 54,
        minSize: 54,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enableHiding: false,
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" justifyContent="center" gap="10px">
            <Box sx={{ width: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {row.original.is_new && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#12a780"
                  }}
                />
              )}
            </Box>
            <Box sx={{ width: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {row.original.has_ai_tags && <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: "#c09560" }} />}
            </Box>
          </Stack>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        Header: () => (
          <MaterialTooltip title="Name" isShow>
            <Typography noWrap>Name</Typography>
          </MaterialTooltip>
        ),
        size: 222,
        enableColumnActions: false,
        enableSorting: true,
        Cell: ({ cell, row }) => {
          const name = cell.getValue<string>();
          const isPdf = row.original.type === FileType["PDF"];
          const isImage = [FileType["PNG"], FileType["JPG"], FileType["JPEG"]].includes(row.original.type as FileType);
          const disabled = !isPdf && !isImage;
          const openFilePreview = async () => {
            if (disabled) return;
            if (isPdf) {
              await openDataExtractor(row.original.id, { initialShowParameters: false });
            } else if (isImage) {
              setFilePreview({
                id: row.original.id,
                name: row.original.name,
                type: row.original.type,
                file_url: row.original.file_url ?? ""
              });
            }
          };
          return (
            <Stack
              flexDirection="row"
              gap="5px"
              alignItems="center"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              <FileRepoIcon type={row.original.type} sx={{ fill: disabled ? "#92A7BC" : "#26a6ae" }} />
              <MaterialTooltip title={name}>
                <Typography
                  noWrap
                  sx={{
                    display: "block",
                    cursor: disabled ? "default" : "pointer",
                    color: disabled ? "#92A7BC" : "#26a6ae",
                    "&:hover": { textShadow: disabled ? "none" : "0.3px 0 0 #26a6ae" }
                  }}
                  onClick={openFilePreview}
                >
                  {name}
                </Typography>
              </MaterialTooltip>
              {row.original.is_k1 && (
                <MaterialTooltip title="This file has a K-1 form — open to review" isShow>
                  <Box
                    sx={{
                      flexShrink: 0,
                      bgcolor: "#26a6ae",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: 700,
                      lineHeight: "14px",
                      letterSpacing: "0.04em",
                      borderRadius: "4px",
                      px: "4px"
                    }}
                  >
                    K-1
                  </Box>
                </MaterialTooltip>
              )}
              {disabled && (
                <MaterialTooltip title="Unsupported file format. Please download to view." isShow>
                  <InfoOutlinedIcon sx={{ fill: "#013951", fontSize: 20 }} />
                </MaterialTooltip>
              )}
            </Stack>
          );
        }
      },
      {
        id: "deal",
        accessorFn: (row) => row.deal?.title ?? "",
        header: "Deal",
        Header: () => (
          <MaterialTooltip title="Deal" isShow>
            <Typography noWrap>Deal</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: true,
        Cell: ({ row }) => {
          const dealId = row.original.deal?.id;
          const dealName = row.original.deal?.title ?? "";

          const handleNavigate = () => {
            if (!dealId) return;
            navigate({ route: "dealWorkspace", params: { deal_id: dealId } });
          };

          const handleEditDeal = async (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: DealPrompt,
              props: {
                currentDealId: dealId ?? null
              }
            });
            if (!result) return;
            updateFile({ fileId: row.original.id, deal_id: result.deal.id });
          };

          return (
            <MaterialTooltip title={dealName}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  overflow: "hidden",
                  "& .edit-icon": { opacity: 0, transition: "opacity 0.15s ease", backgroundColor: "#f5f6f7" },
                  "&:hover .edit-icon": { opacity: 1 }
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    display: "block",
                    color: "#26a6ae",
                    cursor: dealId ? "pointer" : "default",
                    "&:hover": { textShadow: dealId ? "0.3px 0 0 #26a6ae" : "none" }
                  }}
                  onClick={handleNavigate}
                >
                  {dealName}
                </Typography>
                {!isReadOnlyRole && (
                  <Chip
                    className="edit-icon"
                    onClick={handleEditDeal}
                    icon={<EditOutlinedIcon sx={{ fontSize: 14, color: "#496682 !important" }} />}
                    sx={{
                      height: 24,
                      cursor: "pointer",
                      "& .MuiChip-label": { display: "none" },
                      "& .MuiChip-icon": { mx: 0 },
                      minWidth: 32,
                      justifyContent: "center"
                    }}
                  />
                )}
              </Box>
            </MaterialTooltip>
          );
        }
      },
      {
        id: "document_type",
        accessorKey: "document_type",
        header: "Altius Classification",
        size: 220,
        Header: () => (
          <MaterialTooltip title="Altius Classification" isShow>
            <Typography noWrap>Altius Classification</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: true,
        Cell: ({ row }) => {
          const handleClassificationClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: ClassificationPrompt,
              props: {
                currentClassification: row.original.document_type ?? null
              }
            });
            if (!result) return;
            updateFile({ fileId: row.original.id, document_type: result.classification });
          };

          if (!row.original.document_type && isReadOnlyRole) {
            return "";
          }

          if (!row.original.document_type) {
            return <AddValueButton onClick={handleClassificationClick} />;
          }
          return (
            <EditableCell
              disabled={isReadOnlyRole}
              value={row.original.document_type ?? ""}
              onClick={handleClassificationClick}
            />
          );
        }
      },
      {
        id: "investor",
        accessorFn: (row) => row.investor?.name ?? "",
        header: "Investor",
        Header: () => (
          <MaterialTooltip title="Investor" isShow>
            <Typography noWrap>Investor</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const investor = row.original.investor;
          const investorIds = row.original.investor_ids ?? [];
          const isAllInvestors = row.original.all_investors;

          const handleInvestorClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: InvestorPrompt,
              props: {
                currentInvestorId: row.original.investor?.id ?? null,
                currentAllInvestors: row.original.all_investors,
                currentSplitByCommitment: row.original.split_by_commitment,
                currentInvestorIds: row.original.investor_ids,
                currentEntireCommitment: row.original.entire_commitment,
                dealId: row.original?.deal?.id ?? null
              }
            });
            if (!result) return;

            const isGroups = result.investorIds.length;
            updateFile({
              fileId: row.original.id,
              investor_id: result.investor?.id ?? null,
              all_investors: result.allInvestors,
              split_by_commitment: result.splitByCommitment,
              investor_ids: result.investorIds,
              entire_commitment: isGroups ? result.entireCommitment : false,
              group_commitment: isGroups ? !result.entireCommitment : false
            });
          };

          if (isAllInvestors) {
            return <EditableCell disabled={isReadOnlyRole} value="All Investors" onClick={handleInvestorClick} />;
          }

          if (investorIds.length) {
            const groupNames = getGroupNames(investorIds);
            if (groupNames) {
              return <EditableCell disabled={isReadOnlyRole} value={groupNames} onClick={handleInvestorClick} />;
            }
          }
          if (!investor && !investorIds.length && !isAllInvestors) {
            return <AddValueButton onClick={handleInvestorClick} />;
          }

          return <EditableCell disabled={isReadOnlyRole} value={investor?.name || ""} onClick={handleInvestorClick} />;
        }
      },
      {
        id: "currency",
        accessorKey: "currency",
        header: "Currency",
        Header: () => (
          <MaterialTooltip title="Currency" isShow>
            <Typography noWrap>Currency</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const handleCurrencyClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: CurrencyPrompt,
              props: {
                currentCurrency: row.original.currency ?? null
              }
            });
            if (!result) return;
            updateFile({ fileId: row.original.id, currency: result.currency });
          };

          if (!row.original.currency && isReadOnlyRole) {
            return "";
          }

          if (!row.original.currency) {
            return <AddValueButton onClick={handleCurrencyClick} />;
          }
          return <EditableCell disabled={isReadOnlyRole} value={row.original.currency} onClick={handleCurrencyClick} />;
        }
      },
      {
        id: "effective_date",
        accessorKey: "effective_date",
        header: "Effective Date",
        Header: () => (
          <MaterialTooltip title="Effective Date" isShow>
            <Typography noWrap>Effective Date</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const handleDateClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: EffectiveDatePrompt,
              props: {
                currentDate: row.original.effective_date ?? null
              }
            });
            if (!result) return;
            updateFile({
              fileId: row.original.id,
              effective_date: result.effectiveDate ? toIsoMidday(result.effectiveDate) : null
            });
          };
          if (!row.original.effective_date && isReadOnlyRole) {
            return "";
          }

          if (!row.original.effective_date) {
            return <AddValueButton onClick={handleDateClick} />;
          }
          return (
            <EditableCell
              disabled={isReadOnlyRole}
              value={formatDate({ date: row.original.effective_date, format: "DD/MM/YYYY" })}
              onClick={handleDateClick}
            />
          );
        }
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Upload Date",
        Header: () => (
          <MaterialTooltip title="Upload Date" isShow>
            <Typography noWrap>Upload Date</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: true,
        Cell: ({ row }) => (
          <MaterialTooltip title={formatDate({ date: row.original.created_at, format: "DD/MM/YYYY" })}>
            <Typography noWrap sx={{ display: "block", color: "#071f35" }}>
              {formatDate({ date: row.original.created_at, format: "DD/MM/YYYY" })}
            </Typography>
          </MaterialTooltip>
        )
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        Header: () => (
          <MaterialTooltip title="Status" isShow>
            <Typography noWrap>Status</Typography>
          </MaterialTooltip>
        ),
        size: 130,
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const { label, bgcolor, iconColor, Icon } = getStatusConfig(row.original.file_status);
          const handleStatusClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: StatusPrompt,
              props: {
                currentStatus: row.original.file_status
              }
            });
            if (!result) return;
            updateFile({ fileId: row.original.id, file_status: result.status });
          };

          return (
            <MaterialTooltip title={label}>
              <Chip
                disabled={isReadOnlyRole}
                size="small"
                icon={<Icon sx={{ fontSize: 14, color: `${iconColor} !important` }} />}
                label={label}
                onClick={handleStatusClick}
                sx={{
                  bgcolor,
                  color: "#071f35",
                  fontSize: "12px",
                  height: "24px",
                  cursor: "pointer",
                  maxWidth: "100%",
                  "&:hover": { opacity: 0.85 }
                }}
              />
            </MaterialTooltip>
          );
        }
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        Header: () => (
          <MaterialTooltip title="Source" isShow>
            <Typography noWrap>Source</Typography>
          </MaterialTooltip>
        ),
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <MaterialTooltip title={row.original.source}>
            <Chip label={row.original.source} size="small" />
          </MaterialTooltip>
        )
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enableHiding: false,
        enablePinning: true,
        enableColumnOrdering: false,
        size: 110,
        minSize: 110,
        maxSize: 110,
        muiTableHeadCellProps: {
          sx: {
            padding: "0 !important",
            backgroundColor: "transparent !important",
            borderBottom: "1px solid #dbe8f5 !important",
            "&::before": {
              display: "none"
            }
          }
        },
        muiTableBodyCellProps: {
          className: "pinned-actions-cell",
          sx: {
            padding: "0 !important",
            position: "sticky",
            right: 0,
            zIndex: 1,
            backgroundColor: "white",
            borderBottom: "1px solid #dbe8f5 !important",
            "&::before": {
              display: "none"
            }
          }
        },
        Cell: ({ row }) => (
          <Box
            className="pinned-actions-content"
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: "100%",
              px: "8px",
              opacity: 0,
              pointerEvents: "none",
              transition: "opacity 0.15s"
            }}
          >
            <MaterialTooltip isShow title="Download">
              <IconButton
                disabled={!row.original.deal}
                onClick={() =>
                  downloadFileAction({
                    fileId: row.original.id,
                    fileName: row.original.name,
                    dealId: row.original.deal!.id
                  })
                }
                sx={{ padding: "4px" }}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: "#071F35" }} />
              </IconButton>
            </MaterialTooltip>
            {!isReadOnlyRole && (
              <>
                <MaterialTooltip isShow title="Open Data Extractor">
                  <IconButton
                    disabled={row.original.type !== FileType["PDF"]}
                    onClick={() => openDataExtractor(row.original.id)}
                    sx={{ padding: "4px" }}
                  >
                    <ExtratableRepoIcon
                      sx={{
                        width: 16,
                        height: 16,
                        fill: row.original.type !== FileType["PDF"] ? "#bac7ce" : "#071F35"
                      }}
                    />
                  </IconButton>
                </MaterialTooltip>
                <MaterialTooltip isShow title="Delete">
                  <IconButton
                    disabled={!row.original.deal}
                    onClick={() =>
                      row.original.deal && onDelete(row.original.id, row.original.deal.id, row.original.name)
                    }
                    sx={{ padding: "4px" }}
                  >
                    <DeleteIcon sx={{ fill: "rgba(7, 31, 53, 1)" }} />
                  </IconButton>
                </MaterialTooltip>
              </>
            )}
          </Box>
        )
      }
    ],
    [
      filesIds,
      setFile,
      onSelectAll,
      files.length,
      formatDate,
      navigate,
      onDelete,
      refetch,
      updateFile,
      openPrompt,
      openDataExtractor
    ]
  );

  const table = useMaterialReactTable<IFileRepo>({
    layoutMode: "grid",
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableColumnPinning: true,

    columns,
    data: files,
    state: {
      sorting: sorting.orderBy
        ? [
            {
              id:
                Object.keys(sortColumnToHeader).find((k) => sortColumnToHeader[k] === sorting.orderBy) ??
                sorting.orderBy,
              desc: sorting.order === "desc"
            }
          ]
        : [],
      columnVisibility,
      columnPinning: { right: ["actions"] }
    },
    enableHiding: true,
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === "function" ? updater(columnVisibility) : updater;
      onColumnVisibilityChange(next);
    },
    enableSorting: true,
    manualSorting: true,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater([]) : [];
      const first = next[0];
      if (first) {
        const headerName = sortColumnToHeader[first.id] ?? first.id;
        handleSort(headerName);
      }
    },
    enableRowVirtualization: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableStickyHeader: true,
    muiTableContainerProps: {
      ref: containerRef,
      sx: {
        maxHeight: "100%",
        borderRadius: "8px",
        boxShadow: "none",
        border: "1px solid #dbe8f5"
      }
    },
    muiTableHeadCellProps: {
      align: "left",
      sx: {
        backgroundColor: "#f6f8fc !important",
        boxShadow: "none",
        color: "#071f35",
        fontSize: "16px",
        fontWeight: "500",
        border: "none",
        borderBottom: "1px solid #dbe8f5",
        whiteSpace: "nowrap",
        height: "40px",
        textAlign: "left",
        verticalAlign: "middle",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .Mui-TableHeadCell-Content": { alignItems: "center", width: "100%" },
        " .MuiDivider-root": { borderWidth: "1px" },
        minHeight: "40px",
        maxHeight: "40px",
        padding: "8px 20px !important",
        "&:first-of-type": { padding: "0 0 0 12px !important" },
        ".Mui-TableHeadCell-ResizeHandle-Wrapper": {
          cursor: "col-resize",
          paddingRight: "10px"
        },
        "& .Mui-TableHeadCell-ResizeHandle-Divider": {
          borderColor: "#e6e8f2 !important",
          opacity: 0,
          borderWidth: "1px !important",
          transition: "opacity 0.15s"
        },
        "&:hover .Mui-TableHeadCell-ResizeHandle-Divider": {
          opacity: 1
        }
      }
    },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none",
        height: "40px",
        minHeight: "40px",
        "& th": { height: "40px", minHeight: "40px" }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: "white",
        borderBottom: "1px solid #dbe8f5",
        fontSize: "16px",
        color: "#071f35",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        height: "48px",
        minHeight: "48px",
        maxHeight: "48px",
        padding: "12px 20px !important",
        textAlign: "left",
        "&:first-of-type": { padding: "0 0 0 12px" },
        "& .MuiTypography-root, & .MuiChip-label": { fontSize: "16px" }
      }
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        "& [data-can-sort]:not([data-sort]) .MuiTableSortLabel-icon": {
          opacity: "0 !important",
          transition: "opacity 150ms ease-in-out"
        },
        "& [data-can-sort]:hover .MuiTableSortLabel-icon": {
          opacity: "1 !important"
        },
        "& [data-sort] .MuiTableSortLabel-icon": {
          opacity: "1 !important"
        }
      }
    },
    renderBottomToolbarCustomActions: () => null,
    muiTableBodyRowProps: ({ row }) => {
      const isHighlighted = highlightedFileId === row.original.id;
      return {
        sx: {
          backgroundColor: "white",
          height: "48px",
          minHeight: "48px",
          transition: "background-color 0.3s ease",
          "& td": {
            verticalAlign: "middle",
            height: "48px",
            minHeight: "48px",
            transition: "background-color 0.3s ease"
          },
          ...(isHighlighted && {
            backgroundColor: "#ecf1f8 !important",
            "& td": {
              verticalAlign: "middle",
              height: "48px",
              minHeight: "48px",
              backgroundColor: "#ecf1f8 !important",
              transition: "background-color 0.3s ease"
            },
            "& td::after": { backgroundColor: "#ecf1f8" },
            "& .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const }
          }),
          "&:hover": { backgroundColor: "#ecf1f8 !important" },
          "&:hover td": { backgroundColor: "#ecf1f8 !important" },
          "&:hover td::after": { backgroundColor: "#ecf1f8" },
          "&:hover .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const },
          "&:has([aria-expanded='true'])": { backgroundColor: "#ecf1f8 !important" },
          "&:has([aria-expanded='true']) td": { backgroundColor: "#ecf1f8 !important" },
          "&:has([aria-expanded='true']) td::after": { backgroundColor: "#ecf1f8" },
          "&:has([aria-expanded='true']) .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const }
        }
      };
    }
  });

  return (
    <Stack sx={{ borderRadius: "8px", overflow: "hidden", height: "100%" }}>
      <MaterialReactTable table={table} />
      {isDataLoading && isLoading && (
        <Stack justifyContent="center" alignItems="center" sx={{ padding: "10px" }}>
          <CircularProgress />
        </Stack>
      )}
      {filePreview && (
        <FileViewer file={filePreview} isOpen={Boolean(filePreview)} onClose={() => setFilePreview(null)} />
      )}
    </Stack>
  );
};
